import type { GachaMachineApi, GachaNftApi, GachaWinnerApi } from "./gacha-data"

export type GachaApiOptions = {
  bffBaseUrl?: string
  collectorCryptGachaBaseUrl?: string
}

export function getCollectorCryptGachaBaseUrl(options?: GachaApiOptions) {
  return requireGachaEnvValue(
    "NEXT_PUBLIC_COLLECTOR_CRYPT_GACHA_BASE_URL",
    options?.collectorCryptGachaBaseUrl,
  )
}

function getBffBaseUrl(options?: GachaApiOptions) {
  return requireGachaEnvValue("NEXT_PUBLIC_BFF_BASE_URL", options?.bffBaseUrl)
}

function requireGachaEnvValue(name: string, value: string | undefined) {
  if (value) return value

  throw new Error(`[gacha] Missing required hydrated env: ${name}`)
}

export function getGachaApiUrl(path: string, options?: GachaApiOptions) {
  const normalizedBase = getBffBaseUrl(options).replace(/\/$/, "")
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  return `${normalizedBase}/api/gacha${normalizedPath}`
}

export async function fetchGachaJson<T>(
  path: string,
  init?: RequestInit,
  options?: GachaApiOptions,
): Promise<T> {
  const { headers, ...restInit } = init ?? {}
  const requestHeaders = new Headers(headers)
  if (!requestHeaders.has("Accept")) {
    requestHeaders.set("Accept", "application/json")
  }
  if (init?.body && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json")
  }

  const response = await fetch(getGachaApiUrl(path, options), {
    ...restInit,
    cache: init?.cache ?? "no-store",
    headers: Object.fromEntries(requestHeaders.entries()),
  })

  const text = await response.text()
  const data = text ? JSON.parse(text) : null

  if (!response.ok) {
    const message =
      data?.error?.message ||
      data?.message ||
      `Gacha API failed with ${response.status}`
    const details = data?.error?.details || data?.details
    throw new Error(formatGachaErrorMessage(message, details))
  }

  return data as T
}

function formatGachaErrorMessage(message: string, details: unknown) {
  if (
    typeof details !== "string" ||
    details.length === 0 ||
    details === message
  ) {
    return message
  }

  return `${message}: ${details}`
}

export async function postGachaJson<T>(
  path: string,
  body: unknown,
  options?: GachaApiOptions,
  headers?: HeadersInit,
): Promise<T> {
  return fetchGachaJson<T>(
    path,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers,
    },
    options,
  )
}

export type GachaMachinesResponse = {
  machines: GachaMachineApi[]
}

export type GachaStatusResponse = {
  machineStatus?: "running" | "stopped"
  gachas?: Array<{
    code: string
    name?: string
    price?: number
    status?: string
    isOpen?: boolean | null
  }>
}

export type GachaNftsResponse = {
  nfts: GachaNftApi[]
  hasMore?: boolean
  nextCursor?: string | null
  page?: number
  limit?: number
}

export type GachaAssetResponse = {
  nft: GachaNftApi | null
}

export type GachaWinnersResponse = {
  success: boolean
  data: GachaWinnerApi[]
}

export type GachaBuybackAvailabilityResponse = {
  available: boolean
  amount?: number | string
  openedAt?: string | null
}

export type GachaGiftedResponse = {
  sent?: GachaGiftRecord[]
  received?: GachaGiftRecord[]
  perpack?: GachaGiftOpenedPack[]
  totals?: Record<string, unknown>
}

export type GachaGiftRecord = {
  id?: number | string
  memo?: string
  sender?: string
  receiver?: string
  pack_type?: string
  packType?: string
  count?: number
  cost?: number | string
  webhook_confirmed?: boolean
  status?: string
  transaction_signature?: string
  transactionSignature?: string
  created_at?: string
  createdAt?: string
}

export type GachaGiftOpenedPack = {
  pack_opened_id?: number | string
  purchase_type?: string
  purchaseType?: string
  purchase_id?: number | string
  purchaseId?: number | string
  wallet?: string
  pack_type?: string
  packType?: string
  opened_date?: string
  openedDate?: string
  purchase_signature?: string
  purchaseSignature?: string
  send_nft_txn?: string
  sendNftTxn?: string
  memo?: string
  insured_value?: number | string
  insuredValue?: number | string
  pack_quantity?: number
  packQuantity?: number
}

export async function fetchWalletPortfolioNfts(
  wallet: string,
  options?: GachaApiOptions,
): Promise<GachaNftApi[]> {
  const data = await fetchGachaJson<{ nfts: GachaNftApi[] }>(
    `/portfolio?wallet=${encodeURIComponent(wallet)}`,
    undefined,
    options,
  )
  return data.nfts
}

export async function fetchGachaBuybackAvailability(
  {
    wallet,
    nft,
  }: {
    wallet: string
    nft: string
  },
  options?: GachaApiOptions,
): Promise<GachaBuybackAvailabilityResponse> {
  const query = new URLSearchParams({ wallet, nft })
  return fetchGachaJson<GachaBuybackAvailabilityResponse>(
    `/buyback/available?${query.toString()}`,
    undefined,
    options,
  )
}

export async function fetchGachaGifted(
  wallet: string,
  options?: GachaApiOptions,
): Promise<GachaGiftedResponse> {
  return fetchGachaJson<GachaGiftedResponse>(
    `/getGifted?wallet=${encodeURIComponent(wallet)}`,
    undefined,
    options,
  )
}
